/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { getKey } from '../utils/PRIVATE_collectionUtils';

/**
 * Type for scroll position in ListView
 */
export type ListViewScrollPosition<K> = {
  offsetY?: number;
  key?: K;
  y?: number;
};

/**
 * Returns the key for the row at the given index
 */
const getKeyForIndex = (root: HTMLElement, index: number) => {
  if (root != null) {
    const rowElements = root.querySelectorAll(`[role='row']`);
    if (rowElements.length > index) {
      return getKey(rowElements[index] as HTMLElement);
    }
  }
  return null;
};

export function getScrollPosition<K>(root: HTMLDivElement, newScrollTop?: number) {
  // ensure scroll position y value is initialized
  const scrollTop = Math.abs(newScrollTop == null ? root.scrollTop : newScrollTop);

  const scrollPosition: ListViewScrollPosition<K> = {
    y: scrollTop,
    key: undefined,
    offsetY: undefined
  };

  let rowBottom = 0;
  const rowElements = root.querySelectorAll(`[role='row']`);
  if (rowElements.length > 0) {
    if (scrollTop === 0) {
      scrollPosition.key = getKeyForIndex(root, 0) as K;
      scrollPosition.offsetY = 0;
      return scrollPosition;
    }
  }
  for (let i = 0; i < rowElements.length; i++) {
    const rowHeight = rowElements[i].getBoundingClientRect().height;
    rowBottom += rowHeight;
    if (scrollTop < rowBottom) {
      scrollPosition.key = getKeyForIndex(root, i) as K;
      scrollPosition.offsetY = rowHeight + scrollTop - rowBottom;
      break;
    }
  }
  return scrollPosition;
}

/**
 * Helper to set the scroll position on the ListView.
 */
export function setScrollPosition<K>(
  root: HTMLDivElement,
  scrollPosition?: ListViewScrollPosition<K>
) {
  const y = _getScrollTopFromScrollPosition(root, scrollPosition);
  root.scrollTop = y != null ? y : 0;
}

/**
 * Helper to determine the 'y' value of a given scroll position.
 */
function _getScrollTopFromScrollPosition<K>(
  root: HTMLDivElement,
  scrollPosition?: ListViewScrollPosition<K>
) {
  if (scrollPosition != null) {
    let key;
    let useRow = false;
    if (scrollPosition.key != null) {
      // key takes precedence over everything else
      useRow = true;
      key = scrollPosition.key;
    } else if (scrollPosition.offsetY != null && scrollPosition.y == null) {
      useRow = true;
      key = getScrollPosition(root).key;
    }
    if (useRow) {
      if (key != null) {
        let y = 0;
        const rowElements = root.querySelectorAll(`[role ='row']`);
        for (let i = 0; i < rowElements.length; i++) {
          if (key === getKey(rowElements[i] as HTMLElement)) {
            return y + (scrollPosition.offsetY != null ? scrollPosition.offsetY : 0);
          }
          y += rowElements[i].getBoundingClientRect().height;
        }
      }
    } else if (scrollPosition.y != null) {
      return scrollPosition.y;
    }
  }
  // either nothing was provided, or what was provided was invalid
  return undefined;
}
